/**
 * PostHog integration — product analytics for the onboarding funnel.
 *
 * Why: until now, every onboarding decision has been built on intuition. With
 * the first beta cohort about to land, we need to see what they actually do:
 *   • Which OnboardingHero lane wins — chooser (A), concierge (B), explore (C)?
 *   • What fraction of concierge-curious planners actually submit the form?
 *   • Where in the 7-step journey do planners stall?
 *
 * Wiring (mirrors sentry.js):
 *   - main.jsx calls initPosthog() before createRoot
 *   - AuthContext calls setPosthogUser/clearPosthogUser on auth changes
 *   - Feature surfaces (OnboardingHero, ConciergeIntake) call capture() at
 *     surgical funnel events
 *
 * Env vars:
 *   VITE_POSTHOG_KEY   Required for tracking. Without it, init is a no-op —
 *                      local dev / CI / first builds before signup work fine.
 *                      Project API key (starts with `phc_`), not personal.
 *   VITE_POSTHOG_HOST  Optional. Defaults to https://us.i.posthog.com (US
 *                      cloud). Set to https://eu.i.posthog.com for EU
 *                      project or a self-hosted URL.
 *
 * Privacy: autocapture is disabled. We instrument funnel events explicitly so
 * we control exactly what's sent — no DOM-text leaks, no rage-click tracking,
 * no full pageview noise. Session replay is ON for the beta (inputs masked,
 * rendered text visible — see the init config); revisit masking for GDPR when
 * expanding to EU/UK.
 */
import posthog from 'posthog-js'

let initialized = false

export function initPosthog() {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) {
    if (import.meta.env.PROD) {
      console.warn('[posthog] VITE_POSTHOG_KEY not set — analytics disabled')
    }
    return
  }

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    // Tag every event with the environment so prod / preview / dev don't pool.
    // Matches Sentry's environment tagging.
    persistence: 'localStorage+cookie',
    // No autocapture: we want a clean, surgical event stream. Each tracked
    // event is added explicitly in feature code so the schema stays legible.
    autocapture: false,
    // No pageviews — onboarding is a single-page funnel; we capture explicit
    // viewed/submitted events instead. Saves event budget for what matters.
    capture_pageview: false,
    capture_pageleave: false,
    // Session replay ON for the beta. At 1-3 planners, watching a recording of
    // someone move through setup is worth more than any aggregate funnel.
    // maskAllInputs keeps anything TYPED into a field (passwords, card numbers,
    // notes) out of the recording; rendered text (incl. client names/emails) is
    // left visible so the recordings are actually useful for spotting where a
    // planner gets stuck. Tighten via session_recording.maskTextSelector before
    // expanding to EU/UK (GDPR). NOTE: also requires "Session replay" enabled in
    // the PostHog project (Settings → Products → Session replay).
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
    },
    // Default privacy posture for any future capture: mask user input text by
    // default. Inputs we want to capture (source_tool, timing) are passed
    // explicitly via the event payload.
    mask_all_text: false,
    mask_all_element_attributes: false,
    // Identify person by Supabase user id once auth resolves (setPosthogUser).
    // Until then, PostHog generates an anonymous distinct_id and merges on
    // identify — so pre-signup funnel events still roll up to the eventual
    // person.
    bootstrap: undefined,
    loaded: () => { initialized = true },
  })
}

/**
 * Identify the current Supabase user. PostHog will alias the prior anonymous
 * distinct_id onto this id so pre-signup events still attribute correctly.
 *
 * Person properties kept minimal: id (from Supabase) + email. Anything richer
 * (trial_start, business_name, plan) belongs in event properties, not on the
 * person — easier to reason about and cheaper to query.
 */
export function setPosthogUser(user) {
  if (!initialized) return
  if (!user) {
    posthog.reset()
    return
  }
  posthog.identify(user.id, { email: user.email })
}

export function clearPosthogUser() {
  if (!initialized) return
  posthog.reset()
}

/**
 * Capture an event. Safe before init: silently no-ops so feature code doesn't
 * need to gate every call on whether the env var was set.
 */
export function capture(event, properties) {
  if (!initialized) return
  posthog.capture(event, properties)
}

// Re-export for callers that need direct posthog access (rare).
export { posthog }
